import { Emitter } from "@core/events/emitter";

/**
 * `XRPanelSourceRegistry` — the seam between the Vue layer (which owns the
 * offscreen DOM the XR panels rasterize) and the Three.js panel renderer.
 *
 * `XRPanelHost.vue` registers each panel's laid-out DOM element + metadata
 * when an immersive session is active; `XRPanelsRenderer` observes the
 * registry and builds/disposes the textured quads. Registered in the core
 * IOC pass (headless, no three imports) so both sides can resolve it
 * regardless of mount order.
 */

/**
 * Slate density — CSS px to world metres, and the one knob for how big the
 * panels are. Quest's Universal Canvas is 0.0007 (1 canvas px = 0.001 scale ×
 * 0.7); the web runs at **0.0015**, a little over double, because its slates
 * sit at ~0.95 m rather than Quest's 0.5 m (user direction 2026-08-10: "panels
 * are too close to the user, move them 0.5 meter away and make them bigger").
 *
 * Keep the two in step if either moves: the pair is what fixes ANGULAR size,
 * which is what readability actually depends on. At 0.0015 / 0.95 m an 11px
 * row subtends ~1.0°, marginally larger than the 0.98° it had at 0.0007 /
 * 0.45 m — so the panels are physically ~2.1× bigger and read very slightly
 * larger, while sitting at a far more comfortable focal distance.
 *
 * Lives here — the headless seam both sides already import — because
 * `XRPanelHost` needs it too: its slates are content-sized, so it converts a
 * measured pixel height into metres to stack them above the Universal Menu.
 */
export const XR_PANEL_M_PER_PX = 0.0015;

/**
 * The Workflow play slate's id. Exported because it is now named from THREE
 * places — `XRPanelHost` registers it, the media and outline slates dock and
 * slide out of it, and `StepAttachmentRenderer` asks for its world pose to
 * draw the step's leader from. A renamed literal in one of them would silently
 * unhook the others rather than fail.
 */
export const XR_WORKFLOW_PLAY_PANEL_ID = "xr-workflow-play";

export interface XRPanelPlacement {
  /**
   * Yaw around the user's head at spawn, degrees; 0 = straight ahead in the
   * user's initial facing, negative = to their left.
   */
  readonly yawDegrees: number;
  /** Horizontal distance from the head, meters (Quest main slate: 0.5). */
  readonly forwardM: number;
  /** Panel-center height relative to the head, meters (Quest: -0.2). */
  readonly verticalM: number;
  /**
   * Id of a slate this one SLIDES OUT OF: it spawns at that panel's current
   * pose and animates to its own, which is how the step-media slate emerges
   * from behind the play slate rather than materialising beside it. Ignored
   * when the named panel is absent or not yet placed.
   */
  readonly slideFromId?: string;
  /**
   * Whether this slate participates in MAGNETIC docking — dragged one snaps to
   * another magnetic slate's side and thereafter moves with it, until pulled
   * off. Opt-in: the Universal Menu is a permanent bar and has no business
   * being welded to a panel.
   *
   * A docked group is ONE RIGID BODY: its members share a single yaw and their
   * positions are derived from whichever one is moving, so the row stays
   * edge-to-edge however it is dragged. That means a docked slate's own
   * `yawDegrees` stops deciding where it sits — see `XRPanelsRenderer`.
   */
  readonly magnetic?: boolean;
  /**
   * Id of a magnetic slate this one arrives already DOCKED to, so dragging
   * either moves both from the first frame. Requires `magnetic`.
   *
   * Slates that name the same partner CHAIN off each other rather than
   * competing for the one seat beside it, so several children can each name
   * the hub. They chain in arrival order; `yawDegrees` only decides which
   * SIDE they land on, not their order along it.
   */
  readonly dockedToId?: string;
  /**
   * Whether this slate is the HUB of a magnetic dock. Dragging a hub never
   * detaches children — they follow until a child is pulled off on its own.
   * The play slate is the hub; media and outline are children. Requires
   * `magnetic`.
   */
  readonly hub?: boolean;
}

export interface XRPanelSource {
  readonly id: string;
  readonly element: HTMLElement;
  /** CSS px; world size = px × 0.0007 m (Quest Universal Canvas density). */
  readonly widthPx: number;
  readonly heightPx: number;
  readonly placement: XRPanelPlacement;
  /**
   * Changing this asks the renderer to RE-POSE an already-placed slate against
   * its (new) `placement`, animating there rather than snapping.
   *
   * Placement is otherwise a spawn pose that a placed, world-locked slate keeps
   * for the rest of the session — which is what stops a content resize from
   * yanking a panel the wearer has dragged somewhere. This is the explicit
   * opt-in for the one case that genuinely needs to move: the play slate steps
   * aside when the media slate joins it, so the pair straddles the wearer's
   * centre instead of the media panel appearing off to one side.
   */
  readonly reposeKey?: string;
}

export class XRPanelSourceRegistry {
  readonly onChanged = new Emitter<void>();
  private readonly m_sources = new Map<string, XRPanelSource>();

  register(source: XRPanelSource): () => void {
    this.m_sources.set(source.id, source);
    this.onChanged.emit();
    return () => {
      if (this.m_sources.get(source.id) === source) {
        this.m_sources.delete(source.id);
        this.onChanged.emit();
      }
    };
  }

  get sources(): ReadonlyArray<XRPanelSource> {
    return [...this.m_sources.values()];
  }
}

export const XR_PANEL_SOURCE_REGISTRY = Symbol.for("XRPanelSourceRegistry");
